import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  ClipboardList,
  Clock,
  DollarSign,
  Edit,
  Eye,
  FileCheck,
  FileWarning,
  Mail,
  Printer,
  RefreshCw,
  Search,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import apiService from '../services/api';
import { DispatcherDashboardStatsDto, DispatcherQueueItemDto } from '../types';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useDebounce } from '../hooks/useDebounce';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/modals/ConfirmDialog';

type QueueType =
  | 'my-assigned'
  | 'unassigned'
  | 'pending-client-approval'
  | 'nte-exceeded'
  | 'documentation-gaps'
  | 'ready-to-invoice';

interface QueueTab {
  id: QueueType;
  label: string;
  countKey: keyof DispatcherDashboardStatsDto;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const QUEUE_TABS: QueueTab[] = [
  {
    id: 'my-assigned',
    label: 'My Assigned',
    countKey: 'myAssignedOpen',
    icon: UserCheck,
    description: 'Open work orders assigned to you',
  },
  {
    id: 'unassigned',
    label: 'Unassigned',
    countKey: 'unassignedCount',
    icon: Users,
    description: 'Work orders waiting for assignment',
  },
  {
    id: 'pending-client-approval',
    label: 'Client Approval',
    countKey: 'pendingClientApprovalCount',
    icon: Clock,
    description: 'NTE estimates awaiting client approval',
  },
  {
    id: 'nte-exceeded',
    label: 'NTE Exceeded',
    countKey: 'nteExceededCount',
    icon: DollarSign,
    description: 'Estimates over NTE needing action',
  },
  {
    id: 'documentation-gaps',
    label: 'Doc Gaps',
    countKey: 'documentationGapsCount',
    icon: FileWarning,
    description: 'Missing photos or vendor invoice',
  },
  {
    id: 'ready-to-invoice',
    label: 'Ready to Invoice',
    countKey: 'readyToInvoiceCount',
    icon: FileCheck,
    description: 'Completed work ready for invoicing',
  },
];

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const DispatcherDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeQueue, setActiveQueue] = useState<QueueType>('my-assigned');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const debouncedSearch = useDebounce(search, 300);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedItemForEmail, setSelectedItemForEmail] = useState<DispatcherQueueItemDto | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DispatcherQueueItemDto | null>(null);

  const { data: statsResponse, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['dispatcher-dashboard-summary'],
    queryFn: () => apiService.getDispatcherDashboardSummary(),
    refetchInterval: 5 * 60 * 1000,
  });

  const stats = statsResponse?.data;

  useEffect(() => {
    if (statsError) {
      toast.error(statsResponse?.message || 'Unable to load dispatcher dashboard stats');
    }
  }, [statsError, statsResponse?.message]);

  const { data: queueResponse, isLoading: queueLoading, isError: queueError, refetch: refetchQueue } = useQuery({
    queryKey: ['dispatcher-dashboard-queue', activeQueue, page, debouncedSearch],
    queryFn: () =>
      apiService.getDispatcherQueue({
        queueType: activeQueue,
        searchTerm: debouncedSearch || undefined,
        page,
        pageSize,
        sortBy: 'dueDate',
        sortDirection: 'asc',
      }),
    keepPreviousData: true,
  });

  const queueItems: DispatcherQueueItemDto[] = queueResponse?.data?.data ?? queueResponse?.data?.Data ?? [];
  const totalCount = queueResponse?.data?.totalCount ?? queueResponse?.data?.TotalCount ?? 0;
  const totalPages = queueResponse?.data?.totalPages ?? queueResponse?.data?.TotalPages ?? 1;

  useEffect(() => {
    if (queueError) {
      toast.error(queueResponse?.message || 'Unable to load dispatcher queue');
    }
  }, [queueError, queueResponse?.message]);

  const activeTabMeta = useMemo(
    () => QUEUE_TABS.find((tab) => tab.id === activeQueue),
    [activeQueue]
  );

  const handleQueueChange = (queueType: QueueType) => {
    setActiveQueue(queueType);
    setPage(1);
  };

  const handleRefresh = () => {
    refetchStats();
    refetchQueue();
  };

  const handleWorkOrderClick = (workOrderId: number) => {
    navigate(`/workorders/${workOrderId}/edit?from=dispatcher/dashboard`);
  };

  const handleViewJobContactSheet = useCallback((workOrderId: number) => {
    navigate(`/workorders/${workOrderId}/contact?from=dispatcher/dashboard`);
  }, [navigate]);

  const handlePrintWorkOrder = useCallback(async (workOrderId: number) => {
    try {
      const response = await apiService.renderWorkOrder(workOrderId);
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          window.URL.revokeObjectURL(url);
        };
      }
    } catch (error: any) {
      console.error('Error printing work order:', error);
      toast.error(`Error printing work order: ${error?.response?.data?.message || error.message}`);
    }
  }, []);

  const deleteWorkOrderMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteWorkOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatcher-dashboard-summary'] });
      queryClient.invalidateQueries({ queryKey: ['dispatcher-dashboard-queue'] });
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success('Work order deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting work order:', error);
      toast.error('Failed to delete work order');
    },
  });

  const handleDelete = useCallback((item: DispatcherQueueItemDto) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (itemToDelete) {
      deleteWorkOrderMutation.mutate(itemToDelete.workOrderId);
    }
  }, [itemToDelete, deleteWorkOrderMutation]);

  const showEstimateColumns =
    activeQueue === 'pending-client-approval' || activeQueue === 'nte-exceeded';

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-accent-color" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dispatcher Dashboard</h1>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Workflow queues for assignment, approvals, documentation, and invoicing.
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} className="self-start">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {stats?.myOverdue ? (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>
            You have <strong>{stats.myOverdue}</strong> overdue assigned work order{stats.myOverdue === 1 ? '' : 's'}.
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {QUEUE_TABS.map((tab) => {
          const Icon = tab.icon;
          const count = stats?.[tab.countKey] ?? 0;
          const isActive = activeQueue === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleQueueChange(tab.id)}
              className={clsx(
                'rounded-xl border p-4 text-left transition-colors',
                isActive
                  ? 'border-accent-color bg-accent-color/10 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'
              )}
              aria-pressed={isActive}
              aria-label={`${tab.label} queue, ${count} items`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon className={clsx('h-5 w-5', isActive ? 'text-accent-color' : 'text-gray-500')} />
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{tab.label}</span>
                </div>
                <span
                  className={clsx(
                    'rounded-full px-2.5 py-0.5 text-sm font-semibold',
                    isActive
                      ? 'bg-accent-color text-white'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
                  )}
                >
                  {statsLoading ? '…' : count}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{tab.description}</p>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-col gap-4 border-b border-gray-200 p-4 dark:border-gray-700 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {activeTabMeta?.label ?? 'Queue'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {totalCount} item{totalCount === 1 ? '' : 's'}
            </p>
          </div>
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by WO#, client, city, vendor..."
              className="pl-9"
              aria-label="Search queue items"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Work Order</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned</th>
                {showEstimateColumns ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">NTE / Estimate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Approval</th>
                  </>
                ) : null}
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Due</th>
                <th className="min-w-[240px] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {queueLoading ? (
                <tr>
                  <td colSpan={showEstimateColumns ? 8 : 6} className="px-4 py-10 text-center text-gray-500">
                    Loading queue...
                  </td>
                </tr>
              ) : queueItems.length === 0 ? (
                <tr>
                  <td colSpan={showEstimateColumns ? 8 : 6} className="px-4 py-10 text-center text-gray-500">
                    No items in this queue.
                  </td>
                </tr>
              ) : (
                queueItems.map((item) => (
                  <tr
                    key={`${item.queueType}-${item.workOrderId}-${item.estimateId ?? 0}`}
                    className="group hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleWorkOrderClick(item.workOrderId)}
                        className="font-medium text-accent-color hover:underline"
                        aria-label={`Open work order ${item.workOrderNumber}`}
                      >
                        {item.workOrderNumber}
                      </button>
                      {item.isOverdue ? (
                        <span className="ml-2 inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
                          Overdue
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{item.clientName}</div>
                      {[item.locationCity, item.locationState].filter(Boolean).length > 0 ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {[item.locationCity, item.locationState].filter(Boolean).join(', ')}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.statusName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {item.assignedUserName || '—'}
                    </td>
                    {showEstimateColumns ? (
                      <>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          <div>{formatCurrency(item.nteAmount)}</div>
                          <div className="text-xs text-gray-500">{formatCurrency(item.estimateTotal)} est.</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {item.clientApprovalStatus || '—'}
                        </td>
                      </>
                    ) : null}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatDate(item.dueDate)}</td>
                    <td className="relative min-w-[240px] px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="group-hover:opacity-0 transition-opacity duration-200">
                        <div>{item.summary}</div>
                        {item.vendorName ? (
                          <div className="text-xs text-gray-500">Vendor: {item.vendorName}</div>
                        ) : null}
                      </div>
                      <div
                        className="absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewJobContactSheet(item.workOrderId)}
                          className="h-7 px-2 !bg-white dark:!bg-gray-800 !border-gray-300 dark:!border-gray-600 !text-gray-700 dark:!text-gray-300 hover:!bg-gray-50 dark:hover:!bg-gray-600"
                          title="View Job Contact Sheet"
                          aria-label={`View job contact sheet for ${item.workOrderNumber}`}
                        >
                          <span className="inline-flex items-center justify-center h-4 w-4 bg-gray-100 dark:bg-gray-700 rounded">
                            <Eye className="h-3.5 w-3.5" />
                          </span>
                        </Button>
                        <Link
                          to={`/workorders/${item.workOrderId}/edit?from=dispatcher/dashboard`}
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          title="Edit Work Order"
                          aria-label={`Edit work order ${item.workOrderNumber}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          type="button"
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={() => handlePrintWorkOrder(item.workOrderId)}
                          title="Print Work Order"
                          aria-label={`Print work order ${item.workOrderNumber}`}
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={() => {
                            setSelectedItemForEmail(item);
                            setIsEmailModalOpen(true);
                          }}
                          title="Email Work Order"
                          aria-label={`Email work order ${item.workOrderNumber}`}
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDelete(item)}
                          title="Delete Work Order"
                          aria-label={`Delete work order ${item.workOrderNumber}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        Need the full work order grid?{' '}
        <Link to="/workorders" className="text-accent-color hover:underline">
          Go to Work Orders
        </Link>
      </p>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setSelectedItemForEmail(null);
        }}
        title="Email Work Order"
        maxWidth="md"
      >
        {selectedItemForEmail && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Work Order</label>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedItemForEmail.workOrderNumber} - {selectedItemForEmail.clientName}
              </div>
            </div>
            <div>
              <label htmlFor="dispatcherRecipientEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recipient Email <span className="text-red-500">*</span>
              </label>
              <input
                id="dispatcherRecipientEmail"
                type="email"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="name@example.com"
              />
            </div>
            <div>
              <label htmlFor="dispatcherCustomMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Message
              </label>
              <textarea
                id="dispatcherCustomMessage"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                rows={4}
                placeholder="Optional message..."
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEmailModalOpen(false);
                  setSelectedItemForEmail(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const recipientEmail = (document.getElementById('dispatcherRecipientEmail') as HTMLInputElement)?.value;
                  const customMessage = (document.getElementById('dispatcherCustomMessage') as HTMLTextAreaElement)?.value;
                  if (!recipientEmail) {
                    toast.error('Please enter a recipient email address');
                    return;
                  }
                  apiService
                    .sendWorkOrderEmail(selectedItemForEmail.workOrderId, recipientEmail, customMessage || undefined)
                    .then(() => {
                      toast.success('Work order email sent successfully!');
                      setIsEmailModalOpen(false);
                      setSelectedItemForEmail(null);
                    })
                    .catch((error: any) => {
                      console.error('Error sending email:', error);
                      toast.error(`Error sending email: ${error?.response?.data?.message || error.message}`);
                    });
                }}
              >
                Send Email
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Work Order"
        message={`Are you sure you want to delete work order "${itemToDelete?.workOrderNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteWorkOrderMutation.isPending}
      />
    </div>
  );
};

export default DispatcherDashboardPage;
